import type { TFunction } from '@cms/i18n/shared';
import { type FullContext } from '@common/clients/api';
import { type ContextData } from '@common/defaults/ContextData';
import { type RouteParam, type RouteParams } from '@common/defaults/RouteParams';
import { type Route } from '@common/types/Route';
import { isDevelopment } from '@common/utils/env';
import { cleanLeadingSlash } from '@common/utils/UrlUtil';

const translatableRegex = /^[a-z0-9-]+$/i;
const catchAllRegex = /\/\[\[\.\.\..+?\]\]/;

interface FormatRouteParams {
    __url: TFunction | null;
    absolute?: boolean;
    context: Pick<FullContext, 'url' | 'slug'>;
    contextData: Pick<ContextData, 'topLevelCategories' | 'topLevelPages'>;
    params?: RouteParams;
    route: Route;
    searchParams?: Record<string, string>;
}

const getPathParam = (param: RouteParam): string => {
    if (typeof param === 'undefined') {
        return '';
    }

    if (Array.isArray(param)) {
        return param.join('/');
    }

    return param;
};

export const formatRoute = ({
    __url,
    absolute,
    context,
    contextData,
    params,
    route,
    searchParams,
}: FormatRouteParams): string => {
    let path: string = cleanLeadingSlash(route);

    // Handle top level categories
    if (contextData.topLevelCategories && path.startsWith('category-news/')) {
        path = path.slice('category-news/'.length);
    }

    // Handle top level pages
    if (contextData.topLevelPages && path.startsWith('page/')) {
        path = path.slice('page/'.length);
    }

    // Translate pieces
    if (__url) {
        path = path
            .split('/')
            .map((piece) => (translatableRegex.test(piece) ? __url(`url:${piece}`) : piece))
            .join('/');
    }

    // Add routePrefix
    path = path && context.slug ? `/${context.slug}/${path}` : `/${context.slug || ''}${path || ''}`; // Add slash before routePrefix

    if (params) {
        for (const [key, value] of Object.entries(params)) {
            // Adjust the regular expression to include the correct brackets
            const pathParam = getPathParam(value);
            path = path.replace(new RegExp(`\\[\\[?\\.?\\.?\\.?${key}\\]?\\]`, 'g'), pathParam);
        }
    }

    // remove optional catch-all routes, e.g. [[...slug]]
    if (catchAllRegex.test(route)) {
        path = path.replace(catchAllRegex, '');
    }

    if (searchParams) {
        const params = new URLSearchParams(searchParams).toString();

        if (params) {
            path = `${path}?${params}`;
        }
    }

    if (absolute) {
        const protocol = isDevelopment ? 'http' : 'https';
        return `${protocol}://${context.url}${path}`;
    }

    return path;
};
